import React from 'react';
import { Building2, IdCard, Mail, MapPin, Phone, User, Users } from 'lucide-react';

const palette = {
  navy: '#0F2742',
  green: '#1F8A70',
  greenSoft: '#E8F5F2',
  gold: '#C8A45C',
  border: '#DCE7E3',
  surface: '#F7F9FB',
  muted: '#6B7280'
};

const hasMojibake = (value = '') => /[ØÙأک™\uFFFD]/.test(String(value));

const safeValue = (value, fallback = 'غير متوفر') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return hasMojibake(value) ? fallback : value;
};

const normalizeRole = (party = {}) => {
  if (party.type === 'client') return 'العميل';
  if (party.type === 'defendant') return 'المدعى عليه';
  if (party.type === 'plaintiff') return 'المدعي';
  return safeValue(party.role, 'طرف في القضية');
};

const normalizeCapacity = (party = {}) => {
  if (party.capacity === 'individual' || party.capacity === 'فرد') return 'فرد';
  if (party.capacity === 'company' || party.capacity === 'organization' || party.capacity === 'منشأة') return 'منشأة';
  return safeValue(party.capacity, 'فرد');
};

const detailItems = (party) => {
  const items = [];

  if (party.idNumber) {
    items.push({
      key: 'idNumber',
      label: 'رقم الهوية',
      value: safeValue(party.idNumber),
      icon: IdCard
    });
  }

  if (party.crNumber) {
    items.push({
      key: 'crNumber',
      label: 'السجل التجاري',
      value: safeValue(party.crNumber),
      icon: Building2
    });
  }

  if (party.phone) {
    items.push({
      key: 'phone',
      label: 'رقم الجوال',
      value: safeValue(party.phone),
      icon: Phone,
      dir: 'ltr'
    });
  }

  if (party.email) {
    items.push({
      key: 'email',
      label: 'البريد الإلكتروني',
      value: safeValue(party.email),
      icon: Mail,
      dir: 'ltr'
    });
  }

  if (party.address) {
    items.push({
      key: 'address',
      label: 'العنوان',
      value: safeValue(party.address),
      icon: MapPin,
      wide: true
    });
  }

  if (party.representative) {
    items.push({
      key: 'representative',
      label: 'الممثل النظامي',
      value: safeValue(party.representative),
      icon: User,
      wide: true
    });
  }

  return items;
};

const PartiesSection = ({ caseData }) => {
  const parties = Array.isArray(caseData?.parties) ? caseData.parties : [];

  return (
    <section
      style={{
        background: '#FFFFFF',
        border: `1px solid ${palette.border}`,
        borderRadius: 28,
        padding: 28,
        boxShadow: '0 20px 50px rgba(15, 39, 66, 0.06)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 24
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 18,
            background: `linear-gradient(135deg, ${palette.greenSoft} 0%, #ffffff 100%)`,
            border: `1px solid ${palette.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Users size={24} color={palette.green} strokeWidth={2.2} />
        </div>

        <div>
          <h2
            style={{
              margin: 0,
              fontSize: '1.55rem',
              fontWeight: 800,
              color: palette.navy
            }}
          >
            الأطراف
          </h2>
          <p
            style={{
              margin: '6px 0 0',
              color: palette.muted,
              fontSize: '0.95rem'
            }}
          >
            عرض منظم لأطراف القضية وصفاتهم ووسائل التواصل المتاحة.
          </p>
        </div>
      </div>

      {parties.length > 0 ? (
        <div style={{ display: 'grid', gap: 18 }}>
          {parties.map((party, index) => {
            const isClient = party.type === 'client';
            const capacity = normalizeCapacity(party);
            const details = detailItems(party);

            return (
              <article
                key={`${safeValue(party.name, `party-${index}`)}-${index}`}
                style={{
                  background: isClient ? 'linear-gradient(135deg, #FFFCF5 0%, #FFFFFF 100%)' : palette.surface,
                  border: `1px solid ${isClient ? '#E7D3A4' : palette.border}`,
                  borderRadius: 24,
                  padding: 22,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  boxShadow: isClient ? '0 18px 36px rgba(200, 164, 92, 0.08)' : 'none'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: details.length ? 18 : 0,
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 16,
                        background: isClient
                          ? `linear-gradient(135deg, ${palette.gold} 0%, #D9BA78 100%)`
                          : `linear-gradient(135deg, ${palette.navy} 0%, #173A61 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        flexShrink: 0
                      }}
                    >
                      {capacity === 'منشأة' ? (
                        <Building2 size={22} strokeWidth={2.2} />
                      ) : (
                        <User size={22} strokeWidth={2.2} />
                      )}
                    </div>

                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: '1.15rem',
                          fontWeight: 800,
                          color: palette.navy
                        }}
                      >
                        {safeValue(party.name, `الطرف ${index + 1}`)}
                      </h3>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          marginTop: 8,
                          flexWrap: 'wrap'
                        }}
                      >
                        <span
                          style={{
                            padding: '6px 12px',
                            borderRadius: 999,
                            background: isClient ? palette.gold : palette.greenSoft,
                            color: isClient ? '#FFFFFF' : palette.green,
                            fontSize: '0.82rem',
                            fontWeight: 700
                          }}
                        >
                          {normalizeRole(party)}
                        </span>

                        <span
                          style={{
                            padding: '6px 12px',
                            borderRadius: 999,
                            background: '#FFFFFF',
                            border: `1px solid ${palette.border}`,
                            color: palette.muted,
                            fontSize: '0.82rem',
                            fontWeight: 700
                          }}
                        >
                          {capacity}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {details.length > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 14
                    }}
                  >
                    {details.map(({ key, label, value, icon: Icon, dir, wide }) => (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                          padding: '14px 16px',
                          background: '#FFFFFF',
                          border: `1px solid ${palette.border}`,
                          borderRadius: 18,
                          gridColumn: wide ? 'span 2' : 'span 1'
                        }}
                      >
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 12,
                            background: palette.greenSoft,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <Icon size={18} color={palette.green} strokeWidth={2.1} />
                        </div>

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              color: palette.muted,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              marginBottom: 4
                            }}
                          >
                            {label}
                          </div>
                          <div
                            dir={dir}
                            style={{
                              color: palette.navy,
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              lineHeight: 1.7,
                              wordBreak: 'break-word'
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            background: palette.surface,
            border: `1px dashed ${palette.border}`,
            borderRadius: 24,
            padding: 36,
            textAlign: 'center',
            color: palette.muted
          }}
        >
          لا توجد بيانات أطراف متاحة حاليًا.
        </div>
      )}
    </section>
  );
};

export default PartiesSection;
